import type { ComicCharacterCard, ComicProjectCreative } from './comicDomainTypes'
import { normalizeCharacterReferenceUrls } from './comicReferenceLimits'
import type { ComicPanel } from './comicTypes'
import { randomUuid } from './randomUuid'

export type { ComicCharacterCard } from './comicDomainTypes'

const MAX_CHARACTERS = 24
const MAX_NAME = 40
const MAX_APPEARANCE = 600

function clip(raw: unknown, max: number): string {
  const s = typeof raw === 'string' ? raw.trim() : ''
  return s.length > max ? s.slice(0, max) : s
}

export function normalizeCharacterCard(raw: unknown): ComicCharacterCard | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const name = clip(o.name, MAX_NAME)
  if (!name) return null
  const ref =
    typeof o.referenceImageUrl === 'string' && o.referenceImageUrl.trim()
      ? o.referenceImageUrl.trim()
      : null
  return {
    id: typeof o.id === 'string' && o.id.trim() ? o.id.trim() : randomUuid(),
    name,
    appearance: clip(o.appearance, MAX_APPEARANCE),
    referenceImageUrl: ref,
  }
}

export function normalizeCharacterCards(raw: unknown): ComicCharacterCard[] {
  if (!Array.isArray(raw)) return []
  const out: ComicCharacterCard[] = []
  const seen = new Set<string>()
  for (const item of raw) {
    const card = normalizeCharacterCard(item)
    if (!card || seen.has(card.id)) continue
    seen.add(card.id)
    out.push(card)
    if (out.length >= MAX_CHARACTERS) break
  }
  return out
}

/** 从旧版 characterNotes + 全局参考图迁移为角色卡 */
export function migrateCreativeToCharacterCards(input: {
  characterNotes?: string
  characterReferenceUrls?: string[]
  characters?: unknown
}): ComicCharacterCard[] {
  const existing = normalizeCharacterCards(input.characters)
  if (existing.length > 0) return existing

  const notes = (input.characterNotes ?? '').trim()
  const urls = input.characterReferenceUrls ?? []
  if (!notes && urls.length === 0) return []

  const cards: ComicCharacterCard[] = []
  if (notes) {
    const blocks = notes.split(/\n+/).map((b) => b.trim()).filter(Boolean)
    if (blocks.length <= 1) {
      cards.push({
        id: randomUuid(),
        name: '主要角色',
        appearance: notes,
        referenceImageUrl: urls[0] ?? null,
      })
      for (let i = 1; i < urls.length && cards.length < MAX_CHARACTERS; i += 1) {
        cards.push({
          id: randomUuid(),
          name: `参考 ${i + 1}`,
          appearance: '',
          referenceImageUrl: urls[i] ?? null,
        })
      }
    } else {
      blocks.forEach((block, i) => {
        const m = block.match(/^([^：:]+)[：:]\s*(.+)$/s)
        cards.push({
          id: randomUuid(),
          name: m ? clip(m[1], MAX_NAME) : `角色 ${i + 1}`,
          appearance: m ? clip(m[2], MAX_APPEARANCE) : block,
          referenceImageUrl: urls[i] ?? null,
        })
      })
    }
  }
  return normalizeCharacterCards(cards)
}

export function buildCharacterCardsPromptBlock(cards: ComicCharacterCard[]): string {
  const active = cards.filter((c) => c.name && (c.appearance.trim() || c.referenceImageUrl))
  if (active.length === 0) return ''
  const lines = active.map((c) => {
    const parts = [`${c.name}：${c.appearance.trim() || '（见参考图）'}`]
    return parts.join('')
  })
  return ['【角色设定（全作一致）】', ...lines].join('\n')
}

export function characterCardsForPanel(
  creative: ComicProjectCreative,
  panel: ComicPanel,
): ComicCharacterCard[] {
  const all = creative.characters?.length
    ? creative.characters
    : migrateCreativeToCharacterCards(creative)
  const ids = panel.characterIds ?? []
  if (ids.length === 0) return []
  const byId = new Map(all.map((c) => [c.id, c]))
  return ids.map((id) => byId.get(id)).filter((c): c is ComicCharacterCard => Boolean(c))
}

export function panelCharacterPromptSuffix(creative: ComicProjectCreative, panel: ComicPanel): string {
  const picked = characterCardsForPanel(creative, panel)
  if (picked.length === 0) return ''
  return `本格出场角色：${picked.map((c) => `${c.name}（${c.appearance.trim() || '造型见参考图'}）`).join('；')}`
}

export function resolveCharacterReferenceUrls(
  creative: ComicProjectCreative,
  panel?: ComicPanel | null,
): string[] {
  const panelRefs = panel?.referenceImageUrls?.length
    ? normalizeCharacterReferenceUrls(panel.referenceImageUrls)
    : []
  if (panelRefs.length > 0) return panelRefs

  const fromPanelChars = panel
    ? characterCardsForPanel(creative, panel)
        .map((c) => c.referenceImageUrl)
        .filter((u): u is string => Boolean(u?.trim()))
    : []
  if (fromPanelChars.length > 0) {
    return normalizeCharacterReferenceUrls(fromPanelChars)
  }

  const cards = creative.characters?.length
    ? creative.characters
    : migrateCreativeToCharacterCards(creative)
  const fromCards = cards
    .map((c) => c.referenceImageUrl)
    .filter((u): u is string => Boolean(u?.trim()))
  if (fromCards.length > 0) {
    return normalizeCharacterReferenceUrls(fromCards)
  }

  return normalizeCharacterReferenceUrls(creative.characterReferenceUrls)
}

export function normalizePanelCharacterIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  const seen = new Set<string>()
  for (const item of raw) {
    const id = typeof item === 'string' ? item.trim() : ''
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(id)
    if (out.length >= 12) break
  }
  return out
}

export function createEmptyCharacterCard(name = '新角色'): ComicCharacterCard {
  return {
    id: randomUuid(),
    name,
    appearance: '',
    referenceImageUrl: null,
  }
}
